import React, { useState, useEffect } from 'react';
import { Spin, Alert, message, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SimpleDivider from '../../global/SimpleDivider';
import ProductList from './ProductList';
import {
  acceptBulkInvalidAttribute,
  declineBulkInvalidAttribute,
} from '../../../actions/items/ai_quick_fixes/bulk';
import { resetFilter, updateTempFilterItems } from '../../../actions/catalogue/filter/update';
import { AsyncDispatch } from '../../../../types/global';
import { ApplicationState } from '../../../reducers';
import { BulkQuickFixPartType } from '../../../../types/analyses';
import { getTempProductsFilter } from '../../../selectors/catalogue/filterSelector';
import { useAppDispatch } from '../../../store/ReduxStore';

const { Text } = Typography;

type DisplayMode = 'flat' | 'nested';

interface BulkQuickFixPageConfig {
  analysisId: number;
  fetchAction: (brandId: number) => any;
  loadingSelector: (state: ApplicationState) => boolean;
  dataSelector: (state: ApplicationState) => Record<string, BulkQuickFixPartType> | null;
  emptyStateDescription: string;
  displayMode: DisplayMode;
}

interface BulkQuickFixPageProps {
  config: BulkQuickFixPageConfig;
}

const BulkQuickFixPage: React.FC<BulkQuickFixPageProps> = ({ config }) => {
  const dispatch: AsyncDispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [acceptingButtons, setAcceptingButtons] = useState<Set<string>>(new Set());
  const [decliningButtons, setDecliningButtons] = useState<Set<string>>(new Set());

  const brandId = useSelector((state: ApplicationState) => state.parent.brands.selectedBrandId);
  const tempProductsFilter = useSelector(getTempProductsFilter);
  const loading = useSelector(config.loadingSelector);
  const data = useSelector(config.dataSelector);

  const { fetchAction } = config;

  useEffect(() => {
    dispatch(fetchAction(brandId));
  }, [dispatch, brandId, fetchAction]);

  const handleAcceptSuggestion = async (
    buttonKey: string,
    selectedItemPartAttributeIds: number[],
    aiSuggestion: string
  ) => {
    setAcceptingButtons(prev => new Set([...prev, buttonKey]));

    try {
      await dispatch(
        acceptBulkInvalidAttribute(selectedItemPartAttributeIds, aiSuggestion, config.analysisId)
      );
      message.success(t('quickfix:aiSuggestionAcceptedSuccess'));
    } catch (err) {
      message.error(t('quickfix:aiSuggestionBulkAcceptError'));
    } finally {
      setAcceptingButtons(prev => {
        const newSet = new Set(prev);
        newSet.delete(buttonKey);
        return newSet;
      });
    }
  };

  const handleDeclineSuggestion = async (
    buttonKey: string,
    selectedItemPartAttributeIds: number[]
  ) => {
    setDecliningButtons(prev => new Set([...prev, buttonKey]));

    try {
      await dispatch(declineBulkInvalidAttribute(selectedItemPartAttributeIds, config.analysisId));
      message.success(t('quickfix:aiSuggestionDeclinedSuccess'));
    } catch (err) {
      message.error(t('quickfix:aiSuggestionDeclineError'));
    } finally {
      setDecliningButtons(prev => {
        const newSet = new Set(prev);
        newSet.delete(buttonKey);
        return newSet;
      });
    }
  };

  const handleShowProducts = (itemIds: number[]) => {
    dispatch(resetFilter(tempProductsFilter.id));
    dispatch(updateTempFilterItems(tempProductsFilter.id, itemIds));
    setTimeout(() => {
      navigate(`/brand/products/item?brandId=${brandId}&filterId=${tempProductsFilter.id}`);
    }, 100);
  };

  const renderFlatEntries = (partTypeName: string, partTypeData: BulkQuickFixPartType) => {
    return Object.entries(partTypeData)
      .map(([attributeName, attributeData]) => {
        if (!attributeData || typeof attributeData !== 'object') {
          return null;
        }

        const buttonKey = `${partTypeName}-${attributeName}`;
        const isAccepting = acceptingButtons.has(buttonKey);
        const isDeclining = decliningButtons.has(buttonKey);

        return (
          <div key={attributeName} className="analyses__attribute-content">
            <div className="analyses__attribute-header">
              <div className="analyses__title-wrapper">
                <span>
                  <Text type="danger" strong>
                    {t('quickfix:invalid')}
                  </Text>
                  <Text className="ml-2">{attributeName}</Text>
                </span>
                <span className="analyses__part-tag red">
                  {`${(attributeData.items || []).length} ${
                    (attributeData.items || []).length === 1
                      ? t('quickfix:product')
                      : t('quickfix:products')
                  }`}
                </span>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <Text type="success" strong>
                  {t('quickfix:suggested')}
                </Text>
                <Text className="ml-2">{attributeData.ai_suggestion}</Text>
              </div>
            </div>
            <ProductList
              items={attributeData.items || []}
              onConfirm={itemIds =>
                handleAcceptSuggestion(buttonKey, itemIds, attributeData.ai_suggestion)
              }
              onDecline={itemIds => handleDeclineSuggestion(buttonKey, itemIds)}
              onShowProducts={handleShowProducts}
              isLoading={isAccepting}
              isDeclining={isDeclining}
            />
          </div>
        );
      })
      .filter(Boolean);
  };

  const renderNestedEntries = (partTypeName: string, partTypeData: any) => {
    const entries: JSX.Element[] = [];

    Object.entries(partTypeData).forEach(([attributeName, attributeValues]) => {
      if (!attributeValues || typeof attributeValues !== 'object') {
        return;
      }

      entries.push(
        <div
          key={`${partTypeName}-${attributeName}-header`}
          className="font-semibold pl-2 pt-1 pb-2"
        >
          {attributeName}
        </div>
      );

      Object.entries(attributeValues).forEach(([currentValue, attributeData]) => {
        if (!attributeData || typeof attributeData !== 'object') {
          return;
        }

        const buttonKey = `${partTypeName}-${attributeName}-${currentValue}`;
        const isAccepting = acceptingButtons.has(buttonKey);
        const isDeclining = decliningButtons.has(buttonKey);

        entries.push(
          <div key={buttonKey} className="analyses__attribute-content">
            <div className="analyses__attribute-header">
              <div className="analyses__title-wrapper">
                <span>
                  <Text type="danger" strong>
                    {t('quickfix:invalid')}
                  </Text>
                  <Text className="ml-2">{currentValue}</Text>
                </span>
                <span className="analyses__part-tag red">
                  {`${(attributeData.items || []).length} ${
                    (attributeData.items || []).length === 1
                      ? t('quickfix:product')
                      : t('quickfix:products')
                  }`}
                </span>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <Text type="success" strong>
                  {t('quickfix:suggested')}
                </Text>
                <Text className="ml-2">{attributeData.ai_suggestion}</Text>
              </div>
            </div>
            <ProductList
              items={attributeData.items || []}
              onConfirm={itemIds =>
                handleAcceptSuggestion(buttonKey, itemIds, attributeData.ai_suggestion)
              }
              onDecline={itemIds => handleDeclineSuggestion(buttonKey, itemIds)}
              onShowProducts={handleShowProducts}
              isLoading={isAccepting}
              isDeclining={isDeclining}
            />
          </div>
        );
      });
    });

    return entries;
  };

  const renderPartTypeData = () => {
    if (!data) return null;

    return Object.entries(data).map(([partTypeName, partTypeData]) => (
      <div className="mb-10" key={partTypeName}>
        <SimpleDivider title={partTypeName} />
        {config.displayMode === 'flat'
          ? renderFlatEntries(partTypeName, partTypeData)
          : renderNestedEntries(partTypeName, partTypeData)}
      </div>
    ));
  };

  return (
    <div style={{ padding: 24 }}>
      {loading && (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      )}

      {data && !loading && (
        <div>
          {Object.keys(data).length === 0 ? (
            <Alert
              message={t('quickfix:noData')}
              description={t(config.emptyStateDescription)}
              type="info"
            />
          ) : (
            renderPartTypeData()
          )}
        </div>
      )}
    </div>
  );
};

export default BulkQuickFixPage;
